export type AiCoverErrorCode =
  | 'pro_required'
  | 'daily_limit_reached'
  | 'invalid_prompt'
  | 'invalid_reference'
  | 'not_found'
  | 'generation_failed'
  | 'timeout'
  | 'unknown';

const APPROVED_AI_COVER_ERROR_CODES: ReadonlySet<string> = new Set([
  'pro_required',
  'daily_limit_reached',
  'invalid_prompt',
  'invalid_reference',
  'not_found',
  'generation_failed',
  'timeout',
]);

function isApprovedAiCoverErrorCode(value: unknown): value is AiCoverErrorCode {
  return typeof value === 'string' && APPROVED_AI_COVER_ERROR_CODES.has(value);
}

interface GraphQLErrorLike {
  message?: unknown;
}

interface ApolloErrorLike {
  message?: unknown;
  graphQLErrors?: unknown;
}

/**
 * Maps an unknown thrown value (an Apollo `ApolloError`, a plain `Error`, or
 * anything else) to a stable `AiCoverErrorCode`. The backend's error codes
 * ARE the GraphQL error message (see global constraints), so this inspects
 * `graphQLErrors[].message` first — the individual GraphQL errors are the
 * authoritative source — then falls back to the top-level `Error.message`.
 * A raw provider/backend string never leaks through; anything not on the
 * allowlist becomes `'unknown'`.
 */
export function mapAiCoverError(err: unknown): AiCoverErrorCode {
  if (err === null || err === undefined) return 'unknown';

  if (typeof err === 'string') {
    return isApprovedAiCoverErrorCode(err) ? err : 'unknown';
  }

  if (typeof err !== 'object') return 'unknown';

  const { message, graphQLErrors } = err as ApolloErrorLike;

  if (Array.isArray(graphQLErrors)) {
    for (const graphQLError of graphQLErrors) {
      const candidate = (graphQLError as GraphQLErrorLike | undefined)?.message;
      if (isApprovedAiCoverErrorCode(candidate)) return candidate;
    }
  }

  if (isApprovedAiCoverErrorCode(message)) return message;

  return 'unknown';
}
